import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import api from '../../../services/api';
import RepresentationWhitelist from './RepresentationWhitelist';
import VoiceWhitelist from './VoiceWhitelist';

/**
 * Service-group level overrides for client UI + voice/avatar (Meeting 3).
 *
 * Fields layered on top of the platform default:
 *   - audience tag (under_18 / adult / mixed) — drives policy floors
 *   - clientUiOverrides: nav emojis, role label, footer copy, label style, theme
 *   - voiceAndAvatarOverrides: voice catalog whitelist (narrowed), allowed avatar styles
 *
 * Floors enforced server-side:
 *   - under_18 forces showPerBubbleAILabel = true
 *   - under_18 rejects photo_real avatars
 *   - "replacement for human therapy" wording rejected
 *   - role label restricted to AI-containing enum
 */
const ROLE_LABEL_OPTIONS = [
  'AI Assistant',
  'AI Therapist',
  'AI Coach',
  'AI Supporter',
  'AI Companion',
  'AI Guide'
];
const AVATAR_STYLES = ['abstract', 'illustrated', 'semi_realistic', 'photo_real'];
const REJECTED_DISCLAIMER = /replacement\s+for\s+human\s+therapy/i;

const ServiceGroupClientUiSettings = ({ groupId, groupName }) => {
  const [audience, setAudience] = useState('adult');
  const [ui, setUi] = useState({});
  const [va, setVa] = useState({});
  const [platformDefaults, setPlatformDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/service-groups/${groupId}/client-ui`)
      .then((res) => {
        if (cancelled) return;
        setAudience(res.data.audience || 'adult');
        setUi(res.data.clientUiOverrides || { overrideEnabled: false });
        setVa(res.data.voiceAndAvatarOverrides || { overrideEnabled: false });
        setPlatformDefaults(res.data.platformDefaults || null);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load settings'))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [groupId]);

  const isUnder18 = audience === 'under_18';

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (ui.chatDisclaimerFooter?.text && REJECTED_DISCLAIMER.test(ui.chatDisclaimerFooter.text)) {
      setError('Footer text cannot use "replacement for human therapy" wording.');
      return;
    }
    if (isUnder18 && va.avatar?.allowedStyles?.includes('photo_real')) {
      setError('photo_real avatars cannot be allowed for an under_18 audience.');
      return;
    }
    if (isUnder18 && va.avatar?.defaultStyle === 'photo_real') {
      setError('photo_real cannot be the default avatar style for an under_18 audience.');
      return;
    }

    try {
      setSaving(true);
      const res = await api.put(`/service-groups/${groupId}/client-ui`, {
        audience,
        clientUiOverrides: ui,
        voiceAndAvatarOverrides: va
      });
      setUi(res.data.clientUiOverrides || ui);
      setVa(res.data.voiceAndAvatarOverrides || va);
      setSuccess('Service group client UI settings saved.');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('aimhi:clientUiSettingsChanged'));
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const toggleAvatarStyle = (style) => {
    const current = va.avatar?.allowedStyles || [];
    const next = current.includes(style)
      ? current.filter((s) => s !== style)
      : [...current, style];
    setVa({ ...va, avatar: { ...(va.avatar || {}), allowedStyles: next } });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h6" gutterBottom>
        Client UI — {groupName}
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Service-group overrides layer on top of the platform default. Therapists can further override
        a narrower subset for their assigned clients. Floors (under-18 audience, regulatory transparency,
        no photo-real avatars) are enforced server-side regardless of these settings.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="sg-audience">Audience</InputLabel>
        <Select
          labelId="sg-audience"
          label="Audience"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
        >
          <MenuItem value="adult">Adult</MenuItem>
          <MenuItem value="under_18">Under 18 (enforces floors)</MenuItem>
          <MenuItem value="mixed">Mixed</MenuItem>
        </Select>
      </FormControl>

      <Divider sx={{ my: 2 }}>Client UI &amp; AI representations</Divider>

      <FormControlLabel
        control={
          <Switch
            checked={!!ui.overrideEnabled}
            onChange={(e) => setUi({ ...ui, overrideEnabled: e.target.checked })}
          />
        }
        label="Override platform client UI and AI representations"
        sx={{ display: 'block', mb: 2 }}
      />

      {!ui.overrideEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This group inherits the platform client UI and <strong>every AI representation</strong> (the
          avatar + voice + accent bundles) the platform offers. Turn the switch on to choose which
          representations this group uses.
        </Alert>
      )}

      {ui.overrideEnabled && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="sg-role-label">Default role label</InputLabel>
            <Select
              labelId="sg-role-label"
              label="Default role label"
              value={ui.defaultRoleLabel || ''}
              onChange={(e) => setUi({ ...ui, defaultRoleLabel: e.target.value || null })}
            >
              <MenuItem value=""><em>Inherit from platform</em></MenuItem>
              {ROLE_LABEL_OPTIONS.map((l) => (
                <MenuItem key={l} value={l}>{l}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={ui.showNavEmojis === true}
                onChange={(e) => setUi({ ...ui, showNavEmojis: e.target.checked })}
              />
            }
            label="Show emoji prefixes on nav"
            sx={{ display: 'block', mb: 1 }}
          />
          <FormControlLabel
            disabled={isUnder18}
            control={
              <Switch
                checked={ui.showPerBubbleAILabel !== false}
                onChange={(e) => setUi({ ...ui, showPerBubbleAILabel: e.target.checked })}
              />
            }
            label={`Show AI label on every chat bubble${isUnder18 ? ' (forced on for under_18)' : ''}`}
            sx={{ display: 'block', mb: 1 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="sg-bubble-style">Per-bubble label style</InputLabel>
            <Select
              labelId="sg-bubble-style"
              label="Per-bubble label style"
              value={ui.perBubbleAILabelStyle || ''}
              onChange={(e) => setUi({ ...ui, perBubbleAILabelStyle: e.target.value || null })}
            >
              <MenuItem value=""><em>Inherit from platform</em></MenuItem>
              <MenuItem value="full">Full</MenuItem>
              <MenuItem value="subtle">Subtle (corner tag)</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="sg-avatar-display">AI avatar display</InputLabel>
            <Select
              labelId="sg-avatar-display"
              label="AI avatar display"
              value={ui.aiAvatarDisplay || ''}
              onChange={(e) => setUi({ ...ui, aiAvatarDisplay: e.target.value || null })}
            >
              <MenuItem value=""><em>Inherit from platform</em></MenuItem>
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="header">Header only</MenuItem>
              <MenuItem value="grouped">Grouped (first message of an AI run)</MenuItem>
              <MenuItem value="every_message">Every AI message</MenuItem>
            </Select>
          </FormControl>

          <RepresentationWhitelist
            value={ui.allowedRepresentationKeys || []}
            onChange={(next) => setUi({ ...ui, allowedRepresentationKeys: next })}
            helperText="Empty = all platform representations available. Multi-select to narrow to a subset."
          />


          <FormControlLabel
            control={
              <Switch
                checked={ui.chatDisclaimerFooter?.enabled !== false}
                onChange={(e) => setUi({
                  ...ui,
                  chatDisclaimerFooter: { ...(ui.chatDisclaimerFooter || {}), enabled: e.target.checked }
                })}
              />
            }
            label="Show persistent chat footer disclaimer"
            sx={{ display: 'block', mb: 1 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Footer text override"
            value={ui.chatDisclaimerFooter?.text || ''}
            onChange={(e) => setUi({
              ...ui,
              chatDisclaimerFooter: { ...(ui.chatDisclaimerFooter || {}), text: e.target.value || null }
            })}
            helperText='Cannot contain "replacement for human therapy". Leave blank to inherit.'
            sx={{ mb: 2 }}
          />
        </>
      )}

      <Divider sx={{ my: 2 }}>Voices &amp; avatar styles</Divider>

      <FormControlLabel
        control={
          <Switch
            checked={!!va.overrideEnabled}
            onChange={(e) => setVa({ ...va, overrideEnabled: e.target.checked })}
          />
        }
        label="Override platform voice & avatar defaults"
        sx={{ display: 'block', mb: 2 }}
      />

      {!va.overrideEnabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This group inherits the platform voices and avatar styles — every voice the platform offers
          is available to its clients. Turn the switch on to choose which voices and avatar styles
          this group offers. Looking for the avatar + voice bundles? Those are
          <strong> AI representations</strong>, set under "Client UI &amp; AI representations" above.
        </Alert>
      )}

      {va.overrideEnabled && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={va.voiceSelection?.defaultAllowClientSelection === true}
                onChange={(e) => setVa({
                  ...va,
                  voiceSelection: { ...(va.voiceSelection || {}), defaultAllowClientSelection: e.target.checked }
                })}
              />
            }
            label="Allow clients to pick their own voice by default"
            sx={{ display: 'block', mb: 2 }}
          />

          {/* Three admin screens are involved and none of them says so, which
              makes this hard to reason about. Spell out the chain here. */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>How voices and avatars are decided</strong>
            </Typography>
            <Typography variant="body2" component="div">
              1. <strong>Admin → Voices &amp; Avatars</strong> — create the voices and avatars that exist at all.<br />
              2. <strong>Admin → Platform Settings → Voice &amp; avatar</strong> — choose which of them the platform offers
              {platformDefaults && (
                <> (currently {platformDefaults.voiceCatalogIds?.length
                  ? `${platformDefaults.voiceCatalogIds.length} voice${platformDefaults.voiceCatalogIds.length === 1 ? '' : 's'}`
                  : 'no explicit list, so all voices are offered'}).</>
              )}<br />
              3. <strong>Here</strong> — narrow that down for this group only.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This screen can only <strong>narrow</strong> what step 2 allows, never add to it. Leave a list empty to
              inherit everything from the level above.
            </Typography>
          </Alert>

          <VoiceWhitelist
            value={(va.voiceSelection?.allowedVoiceCatalogIds || []).map(String)}
            platformVoiceIds={platformDefaults?.voiceCatalogIds || null}
            onChange={(ids) => setVa({
              ...va,
              voiceSelection: { ...(va.voiceSelection || {}), allowedVoiceCatalogIds: ids }
            })}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Allowed avatar styles</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {AVATAR_STYLES.map((style) => {
              const isOn = (va.avatar?.allowedStyles || []).includes(style);
              const disabled = isUnder18 && style === 'photo_real';
              return (
                <Chip
                  key={style}
                  label={style}
                  color={isOn ? 'primary' : 'default'}
                  onClick={disabled ? undefined : () => toggleAvatarStyle(style)}
                  disabled={disabled}
                />
              );
            })}
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="sg-avatar-default">Default avatar style</InputLabel>
            <Select
              labelId="sg-avatar-default"
              label="Default avatar style"
              value={va.avatar?.defaultStyle || ''}
              onChange={(e) => setVa({
                ...va,
                avatar: { ...(va.avatar || {}), defaultStyle: e.target.value || null }
              })}
            >
              <MenuItem value=""><em>Inherit from platform</em></MenuItem>
              <MenuItem value="abstract">Abstract</MenuItem>
              <MenuItem value="illustrated">Illustrated</MenuItem>
              <MenuItem value="semi_realistic">Semi-realistic</MenuItem>
              <MenuItem value="photo_real" disabled={isUnder18}>
                Photo-real{isUnder18 ? ' (not allowed for under_18)' : ''}
              </MenuItem>
            </Select>
          </FormControl>
        </>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default ServiceGroupClientUiSettings;
